import { useState, useEffect, useCallback } from 'react'
import { fetchWithAuth } from '@/lib/api'

export interface Space {
  id: string
  organization_id: string
  name: string
  path: string
  visibility: string
  created_at: string
  updated_at: string
}

/** Fetch spaces the current user can access for a workspace */
export function useSpaces(organizationId: string | null) {
  const [spaces, setSpaces] = useState<Space[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(async () => {
    if (!organizationId) {
      setSpaces([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/organizations/${organizationId}/spaces`)
      if (!res.ok) {
        if (res.status === 403 || res.status === 404) {
          setSpaces([])
          return
        }
        throw new Error(`Failed to fetch spaces: ${res.status}`)
      }
      const data: Space[] = await res.json()
      setSpaces(data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      setSpaces([])
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { spaces, loading, error, refetch }
}

/** Fetch spaces for multiple workspaces. Returns orgId -> Space[] map. */
export function useSpacesForWorkspaces(workspaceIds: string[]) {
  const [spacesByOrg, setSpacesByOrg] = useState<Record<string, Space[]>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (workspaceIds.length === 0) {
      setSpacesByOrg({})
      return
    }
    let cancelled = false
    setLoading(true)
    Promise.all(
      workspaceIds.map(async (orgId) => {
        const res = await fetchWithAuth(`/api/organizations/${orgId}/spaces`)
        if (!res.ok) return { orgId, spaces: [] }
        const spaces: Space[] = await res.json()
        return { orgId, spaces: spaces ?? [] }
      })
    ).then((results) => {
      if (cancelled) return
      const map: Record<string, Space[]> = {}
      for (const { orgId, spaces } of results) {
        map[orgId] = spaces
      }
      setSpacesByOrg(map)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [workspaceIds.join(',')])

  return { spacesByOrg, loading }
}
